import { useEffect, useRef, useState } from 'react';
import { feedRequest } from '../../lib/feedApi';
import type { FeedPreferences } from '../../shared/utils/feed-preferences';

/** Sunucu onaylı gizleme; hesap değişimi, yinelenen tıklama ve geç yanıtlar yalıtılır. */
export default function useFeedPreferences(owner: string | undefined) {
  const [state, setState] = useState({
    owner,
    ids: [] as string[],
    ready: false,
    busy: false,
    error: '',
    undo: [] as string[],
  });
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const request = useRef<AbortController | null>(null);
  const version = useRef(0);
  const lock = useRef(false);
  const ownState = state.owner === owner ? state : undefined;

  const read = async () => {
    if (!owner || lock.current) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const own = ++version.current;
    try {
      const result = await feedRequest<FeedPreferences>(
        '/api/feed-preferences',
        'GET',
        undefined,
        controller.signal,
      );
      if (controller.signal.aborted || currentOwner.current !== owner || version.current !== own)
        return;
      setState(previous => ({
        ...previous,
        owner,
        ids: result.hiddenEventIds,
        ready: true,
        error: '',
      }));
    } catch {
      if (!controller.signal.aborted && currentOwner.current === owner && version.current === own)
        setState(previous => ({ ...previous, owner, error: 'preferencesError' }));
    }
  };

  useEffect(() => {
    lock.current = false;
    setState({ owner, ids: [], ready: false, busy: false, error: '', undo: [] });
    read();
    const focus = () => {
      read();
    };
    window.addEventListener('focus', focus);
    return () => {
      request.current?.abort();
      version.current++;
      window.removeEventListener('focus', focus);
    };
  }, [owner]);

  const change = async (ids: string[], hidden: boolean, restoreAll = false) => {
    if (!owner || !ownState?.ready || lock.current) return;
    lock.current = true;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const own = ++version.current;
    const undo = hidden ? ids.filter(id => !ownState.ids.includes(id)) : [];
    setState(previous => ({ ...previous, busy: true, error: '' }));
    try {
      const result = await feedRequest<FeedPreferences>(
        '/api/feed-preferences',
        'PUT',
        restoreAll ? { restoreAll: true } : { eventIds: ids, hidden },
        controller.signal,
      );
      if (controller.signal.aborted || currentOwner.current !== owner || version.current !== own)
        return;
      setState({ owner, ids: result.hiddenEventIds, ready: true, busy: false, error: '', undo });
    } catch (error) {
      if (!controller.signal.aborted && currentOwner.current === owner && version.current === own)
        setState(previous => ({
          ...previous,
          busy: false,
          error:
            error instanceof Error && error.message === '409'
              ? 'preferencesLimit'
              : 'preferencesSaveError',
        }));
    } finally {
      if (currentOwner.current === owner && version.current === own) lock.current = false;
    }
  };
  return {
    hidden: new Set(ownState?.ids || []),
    ready: !owner || !!ownState?.ready,
    busy: !!ownState?.busy,
    error: ownState?.error || '',
    undo: ownState?.undo || [],
    retry: read,
    change,
  };
}
